import {
    Box,
    Flex,
    HStack,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { summarizePriceMatchEntry } from '../../../../components/settings/priceMatchCriteria';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
    SettingsCard,
    SettingsChoiceCards,
    SettingsNumberField,
    SettingsRow,
} from '../../../../components/settings/primitives';
import { useSelectionDraft } from './shared';
import type { SelectionSectionProps } from './types';

const CONSOLIDATION_TYPES = ['dollar', 'percentage'] as const;

/** The two ways Yaxle can price match a non-DRP job. */
const NON_DRP_PRICE_MATCH_CHOICES = [
    {
        value: 'estimate' as const,
        label: 'Match to estimate list price',
        description: 'Price match against the list price written on the estimate.',
    },
    {
        value: 'custom' as const,
        label: 'Custom price matching order',
        description: 'Build the cards Yaxle price matches against, in priority order.',
    },
];

const SelectionSection = ({ data, oemOptions }: SelectionSectionProps) => {
    const navigate = useNavigate();
    const { selection, updateSelection } = useSelectionDraft(data);

    const handleChangeNonDrpPriceMatchMode = (mode: 'estimate' | 'custom') => {
        if (mode === 'estimate') {
            updateSelection({ nonDrpPriceMatch: 'estimate' });
            return;
        }
        // The order is built on its own screen, which saves the mode with it:
        // choosing custom without any cards would say nothing about the order.
        navigate('/settings/selection/price-match');
    };

    // const handleChangePriceMatchToAllowableThreshold = (value: number) => {
    //     const rounded = Math.round(value) || 0;
    //     const updates: Partial<SelectionSettings> = { priceMatchToAllowableThreshold: rounded };
    //     // The flag threshold can never sit below the auto price match threshold.
    //     if (selection.allowableFlagThreshold < rounded) {
    //         updates.allowableFlagThreshold = rounded;
    //     }
    //     updateSelection(updates);
    // };

    const nonDrpRanking = Array.isArray(selection.nonDrpPriceMatch) ? selection.nonDrpPriceMatch : [];

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard title="Delivery" description="How long Yaxle will wait for a part before it prefers another option.">
                <SettingsRow
                    label="On-site delivery timeline preference"
                    description="Parts with delivery times under this value will be selected, regardless of margin."
                    control={
                        <SettingsNumberField
                            value={Math.round(selection.preferredDeliveryTime / 24)}
                            onChange={(next) =>
                                updateSelection({ preferredDeliveryTime: next * 24 })
                            }
                            allowEmpty={false}
                            min={1}
                            max={28}
                            width="80px"
                            unit="days"
                            ariaLabel="On-site delivery timeline preference"
                        />
                    }
                />

                <SettingsRow
                    label="Absolute maximum delivery timeline"
                    description="Automatically flag parts if all options have delivery times greater that this value."
                    control={
                        <SettingsNumberField
                            value={Math.round(selection.maximumDeliveryTime / 24)}
                            onChange={(next) =>
                                updateSelection({ maximumDeliveryTime: next * 24 })
                            }
                            allowEmpty={false}
                            min={1}
                            max={28}
                            width="80px"
                            unit="days"
                            ariaLabel="Absolute maximum delivery timeline"
                        />
                    }
                />

                <SettingsRow
                    label="Can order backordered parts"
                    description="Allow Yaxle to auto-order a backordered part when it's the only option."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={selection.canOrderBackordered}
                            onChange={(e) => updateSelection({ canOrderBackordered: e.target.checked })}
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard title="Consolidation">
                <SettingsRow
                    label="Supplier consolidation threshold"
                    description="What margin or dollar amount you're willing to sacrifice to order from fewer suppliers on the same order."
                    control={
                        <HStack spacing={3}>
                            <HStack spacing={0} bg="gray.100" borderRadius="md" p="2px">
                                {CONSOLIDATION_TYPES.map((typeOption) => {
                                    const isActive = selection.supplierConsolidation.type === typeOption;
                                    return (
                                        <Box
                                            key={typeOption}
                                            as="button"
                                            type="button"
                                            px={3}
                                            py={1}
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={isActive ? 'gray.200' : 'transparent'}
                                            bg={isActive ? 'white' : 'transparent'}
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={isActive ? 'gray.800' : 'gray.500'}
                                            transition="all 0.15s ease"
                                            _hover={!isActive ? { color: 'gray.700' } : undefined}
                                            onClick={() => {
                                                if (!isActive) {
                                                    updateSelection({
                                                        supplierConsolidation: { type: typeOption, value: 0 },
                                                    });
                                                }
                                            }}
                                        >
                                            {typeOption === 'dollar' ? 'Dollar' : 'Percent'}
                                        </Box>
                                    );
                                })}
                            </HStack>
                            <SettingsNumberField
                                value={
                                    selection.supplierConsolidation.type === 'percentage'
                                        ? Math.round(selection.supplierConsolidation.value * 100)
                                        : selection.supplierConsolidation.value
                                }
                                onChange={(next) => {
                                    const isPercent =
                                        selection.supplierConsolidation.type === 'percentage';
                                    updateSelection({
                                        supplierConsolidation: {
                                            type: selection.supplierConsolidation.type,
                                            value: isPercent ? next / 100 : next,
                                        },
                                    });
                                }}
                                allowEmpty={false}
                                min={0}
                                max={
                                    selection.supplierConsolidation.type === 'percentage'
                                        ? 100
                                        : 500
                                }
                                width="100px"
                                prefix={
                                    selection.supplierConsolidation.type === 'dollar'
                                        ? '$'
                                        : undefined
                                }
                                unit={
                                    selection.supplierConsolidation.type === 'percentage'
                                        ? '%'
                                        : undefined
                                }
                                textAlign="right"
                                ariaLabel="Supplier consolidation threshold"
                            />
                        </HStack>
                    }
                />
            </SettingsCard>

            <SettingsCard title="Price matching">
                {/* <SettingsRow
                    label="Price match to insurance allowable threshold"
                    description="How much lower you're willing to price match to remove advisor scores on parts."
                    control={
                        <SettingsNumberField
                            value={selection.priceMatchToAllowableThreshold}
                            onChange={handleChangePriceMatchToAllowableThreshold}
                            allowEmpty={false}
                            min={0}
                            max={500}
                            width="100px"
                            prefix="$"
                            ariaLabel="Price match to insurance allowable threshold"
                        />
                    }
                />

                <SettingsRow
                    label="Flag parts with an advisor score"
                    description="Flag parts when there's a price difference greater than this amount between our price match and CCC's advisor score. Cannot be lower than the setting above."
                    control={
                        <SettingsNumberField
                            value={selection.allowableFlagThreshold}
                            onChange={(next) => updateSelection({ allowableFlagThreshold: next })}
                            allowEmpty={false}
                            min={selection.priceMatchToAllowableThreshold}
                            max={500}
                            width="100px"
                            prefix="$"
                            ariaLabel="Flag parts with an advisor score"
                        />
                    }
                /> */}

                <SettingsRow
                    label="Non-DRP price match"
                    description="For non-DRP jobs, choose how Yaxle price matches: to the estimate list price, or using a custom ranking of price-match targets."
                >
                    <VStack align="stretch" spacing={3}>
                        <SettingsChoiceCards
                            value={selection.nonDrpPriceMatch === 'estimate' ? 'estimate' : 'custom'}
                            onChange={handleChangeNonDrpPriceMatchMode}
                            choices={NON_DRP_PRICE_MATCH_CHOICES}
                        />

                        {selection.nonDrpPriceMatch === 'estimate' ? (
                            <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                                <Flex justify="space-between" align="center" gap={6}>
                                    <Box minW={0}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                            Maintain selections with estimate as written
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" mt={0.5}>
                                            It always maintains the part types on the estimate, unless there's an OEM part available with a higher margin while price matching.
                                        </Text>
                                    </Box>
                                    <Switch
                                        size="md"
                                        colorScheme="partbay-blue"
                                        isChecked={selection.nonDrpMirrorEstimateTypes}
                                        onChange={(e) => updateSelection({ nonDrpMirrorEstimateTypes: e.target.checked })}
                                    />
                                </Flex>
                            </Box>
                        ) : (
                            <Box
                                as="button"
                                type="button"
                                onClick={() => navigate('/settings/selection/price-match')}
                                textAlign="left"
                                w="100%"
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="gray.50"
                                _hover={{ borderColor: 'gray.300', bg: 'gray.100' }}
                                transition="all 0.15s"
                                p={3}
                            >
                                <HStack justify="space-between" align="center" mb={nonDrpRanking.length > 0 ? 2 : 0}>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                                        Custom order
                                    </Text>
                                    <Text fontSize="xs" color="blue.500" fontWeight="medium">
                                        Edit
                                    </Text>
                                </HStack>
                                {nonDrpRanking.length > 0 ? (
                                    <VStack align="stretch" spacing={1}>
                                        {nonDrpRanking.map((entry, index) => (
                                            <HStack key={index} spacing={2} align="flex-start">
                                                <Flex
                                                    align="center"
                                                    justify="center"
                                                    w="18px"
                                                    h="18px"
                                                    borderRadius="full"
                                                    bg={PARTBAY_BLUE_TINT}
                                                    color={PARTBAY_BLUE}
                                                    fontSize="0.65rem"
                                                    fontWeight="bold"
                                                    flexShrink={0}
                                                    mt="1px"
                                                >
                                                    {index + 1}
                                                </Flex>
                                                <Text fontSize="xs" color="gray.700">
                                                    {summarizePriceMatchEntry(entry)}
                                                </Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                ) : (
                                    <Text fontSize="xs" color="gray.500">
                                        No entries configured yet. Click to add.
                                    </Text>
                                )}
                            </Box>
                        )}
                    </VStack>
                </SettingsRow>
            </SettingsCard>
        </VStack>
    );
};

export default SelectionSection;
